import { FunnelStepSettingTabsEnum } from 'modules/funnels/funnel/enums/funnel-step-setting-tabs-enum';
import { useUpdateFunnelStepQuery } from 'modules/funnels/funnel/hooks/use-update-funnel-step-query';
import { FunnelStepInterface } from 'modules/funnels/funnel/types/funnel-step-interface';
import { getSelectedFunnelStepCookie, removeFunnelStepCookie } from 'modules/funnels/funnel/utils/cookie-utils';
import { FunnelId } from 'modules/funnels/types/funnel-interface';
import { getFunnelUrl } from 'modules/funnels/utils/get-funnel-url';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { getFunnelStepInDB, setSelectedFunnelStepInDB, updateFunnelStepInDB } from 'shared/db/utils/funnels';
import useMediaQuery from 'shared/hooks/use-media-query';
interface UseFunnelStepQueryProps {
  funnelStepsList: FunnelStepInterface[] | undefined;
  setSelectedFunnelStep: (funnelStep: FunnelStepInterface | null) => void;
}
export const useFunnelStepQuery = ({
  funnelStepsList,
  setSelectedFunnelStep
}: UseFunnelStepQueryProps) => {
  const {
    isXS: isMobile
  } = useMediaQuery();
  const router = useRouter();
  const funnelId = parseInt(router.query.funnelId as string) as FunnelId;
  const selectedFunnelStepId = router.query.step ? parseInt(router.query.step as string) : undefined;
  const selectedFunnelStepTab = router.query.tab ? router.query.tab as FunnelStepSettingTabsEnum : undefined;
  const {
    updateFunnelStepQuery
  } = useUpdateFunnelStepQuery();
  const stepFromCookie = getSelectedFunnelStepCookie(funnelId);
  useEffect(() => {
    if (!funnelStepsList) return;
    const handleStepSelection = async () => {
      const stepFromIndexDB = await getFunnelStepInDB(funnelId);
      if (selectedFunnelStepId) {
        await handleSelectedStepFromProps(selectedFunnelStepId);
        return;
      }
      if (stepFromCookie) {
        await handleStepFromCookie(stepFromCookie);
        return;
      }
      if (stepFromIndexDB) {
        await handleStepFromIndexDB(stepFromIndexDB);
        return;
      }
      await handleDefaultStepSelection();
    };
    handleStepSelection();
  }, [funnelStepsList, funnelId, selectedFunnelStepId, stepFromCookie]);
  const handleSelectedStepFromProps = async (selectedFunnelStepId_0: number) => {
    const selectedStep = findStepById(selectedFunnelStepId_0);
    if (!selectedStep) {
      await resetFunnelStep();
      return;
    }
    await setStepAndUpdateStorage(selectedStep);
  };
  const resetFunnelStep = async () => {
    if (!funnelStepsList || funnelStepsList.length === 0) {
      router.replace(getFunnelUrl(funnelId));
      return;
    }
    await setFirstStepAsDefault();
  };
  const handleStepFromCookie = async (stepFromCookie_0: string) => {
    const stepId = Number(stepFromCookie_0);
    const selectedStep_0 = findStepById(stepId);
    if (!selectedStep_0) {
      await resetFunnelStep();
      return;
    }
    await setStepAndUpdateStorage(selectedStep_0);
    removeFunnelStepCookie(funnelId);
  };
  const handleStepFromIndexDB = async (stepFromIndexDB_0: number) => {
    const selectedStep_1 = findStepById(stepFromIndexDB_0);
    if (!selectedStep_1) return;
    setSelectedFunnelStep(selectedStep_1);
    updateFunnelStepQuery(stepFromIndexDB_0, !isMobile ? FunnelStepSettingTabsEnum.stepConfiguration : undefined);
  };
  const handleDefaultStepSelection = async () => {
    if (!funnelStepsList || funnelStepsList.length === 0) return;
    await setFirstStepAsDefault();
  };
  const setFirstStepAsDefault = async () => {
    if (!funnelStepsList || funnelStepsList.length === 0) return;
    const firstStep = funnelStepsList[0];
    if (!firstStep) return;
    setSelectedFunnelStep(firstStep);
    await setSelectedFunnelStepInDB(funnelId, firstStep.id);
    updateFunnelStepQuery(firstStep.id, !isMobile ? FunnelStepSettingTabsEnum.stepConfiguration : undefined);
  };
  const setStepAndUpdateStorage = async (step: FunnelStepInterface) => {
    setSelectedFunnelStep(step);
    await updateFunnelStepInDB(funnelId, step.id);
    updateFunnelStepQuery(step.id);
  };
  const findStepById = (stepId_0: number): FunnelStepInterface | undefined => {
    if (!funnelStepsList) return undefined;
    return funnelStepsList.find(step_0 => step_0.id === stepId_0);
  };
  return {
    selectedFunnelStepId,
    selectedFunnelStepTab
  };
};